<!-- Ported from the Polaris kit: src/product/components/OptionSelect.vue.
     Classes translated to Tailwind 3 by rebranding_frontend/port-kit-component.mjs;
     strings moved to i18n by hand. Change the design in the kit first. -->
<script setup>
// One value out of a short list, in a field or a toolbar. A composition of the registry Select, so that every choice
// of the product opens the same menu (the product's font, neutral highlight, the check at the right) and never the system's own,
// which cannot follow the theme. The list is data: strings, or { value, label, disabled }. A value of '' means "none"
// (the registry Select cannot hold an empty value, so it travels as a private word and comes back as ''). The trigger
// takes what a control takes (id, aria-label, aria-describedby, aria-invalid, class) and says the current value in
// `data-value`, and each item its own, so that checks read values and not only words.
import { computed } from 'vue';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const props = defineProps({
  modelValue: { type: null, default: '' },
  options: { type: Array, required: true },
  placeholder: { type: String, default: '' },
  size: { type: String, default: 'default' }, // 'default' is 36px high, 'sm' is 32px (toolbars, the inspector)
  disabled: Boolean,
});
const emit = defineEmits(['update:modelValue']);
defineOptions({ inheritAttrs: false });
const NONE = '__none__';
const items = computed(() =>
  props.options.map(option =>
    option !== null && typeof option === 'object'
      ? option
      : { value: option, label: String(option) }
  )
);
const inside = value =>
  value === '' || value === null || value === undefined ? NONE : value;
const outside = value => (value === NONE ? '' : value);
// The trigger says the label itself, worked out from the options and the value (the placeholder when nothing is chosen).
// reka's SelectValue keeps the text an item had when it mounted, so after a change of language, or of the options,
// it would go on saying the old words.
const selectedLabel = computed(
  () =>
    items.value.find(
      option => inside(option.value) === inside(props.modelValue)
    )?.label ?? props.placeholder
);
</script>

<template>
  <Select
    :model-value="inside(modelValue)"
    :disabled="disabled"
    @update:model-value="value => emit('update:modelValue', outside(value))"
  >
    <SelectTrigger
      v-bind="$attrs"
      :size="size"
      class="w-full"
      :data-value="modelValue ?? ''"
    >
      <SelectValue :placeholder="placeholder">{{ selectedLabel }}</SelectValue>
    </SelectTrigger>
    <SelectContent>
      <SelectItem
        v-for="option in items"
        :key="String(option.value)"
        :value="inside(option.value)"
        :data-value="String(option.value)"
        :disabled="option.disabled"
      >
        {{ option.label }}
      </SelectItem>
    </SelectContent>
  </Select>
</template>
